import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Table, Button, Modal, Form, Input, Select, InputNumber,
  Space, Typography, Tag, Tooltip, App, Breadcrumb, Divider,
} from 'antd';
import { PlusOutlined, EditOutlined, RightOutlined, MinusCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../context/AuthContext';
import { coursesApi } from '../api/courses';
import { modulesApi } from '../api/modules';
import { Course, CourseModule, ModuleGroup, Status } from '../api/types';
import { DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableRow, DragHandle, useSortableSensors } from '../components/SortableTable';
import { reorderByDrag } from '../components/reorder';

const { Title } = Typography;

const STATUS_COLORS: Record<Status, string> = {
  Enabled: 'success',
  Disabled: 'warning',
  Deleted: 'error',
};

const STATUS_OPTIONS: Status[] = ['Enabled', 'Disabled', 'Deleted'];

const GROUP_OPTIONS: ModuleGroup[] = ['Default', 'Pickup'];

/** The module modal's field set — every CourseModule property except the two
 *  the form never renders. Typing the form instance with this is what makes a
 *  missing or misspelled field a compile error rather than a runtime surprise. */
type ModuleFormValues = Omit<CourseModule, 'id' | 'courseId'>;

const ModulesPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { apiClient } = useAuth();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CourseModule | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<ModuleFormValues>();

  const sensors = useSortableSensors();

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const next = reorderByDrag(modules, String(active.id), String(over.id));
    if (next === modules) return;
    setModules(next);
    mApi()
      .reorder(Number(courseId), next.map((m) => ({ id: m.id, sortOrder: m.sortOrder })))
      .then((res) => setModules(res.data.items))
      .catch(() => {
        message.error('Failed to reorder modules');
        fetchData();
      });
  };

  const cApi = useCallback(() => coursesApi(apiClient!), [apiClient]);
  const mApi = useCallback(() => modulesApi(apiClient!), [apiClient]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [courseRes, modulesRes] = await Promise.all([
        cApi().getById(Number(courseId)),
        mApi().getList(Number(courseId)),
      ]);
      setCourse(courseRes.data);
      setModules(modulesRes.data.items);
    } catch {
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [cApi, mApi, courseId, message]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      title: '', description: '', descriptionExtended: '', imageUrl: '', status: 'Enabled', group: 'Default', sortOrder: modules.length,
      hook: { question: '', answer: '' }, outcomes: [], reviews: [], keyStatements: [],
      priceRub: 0, discountPercent: null,
    });
    setModalOpen(true);
  };

  const openEdit = (mod: CourseModule) => {
    setEditing(mod);
    // The API returns null for the optional JSON fields, but Form.List and the
    // nested hook items want a container. Normalize here so the modal opens on
    // the same shape openCreate seeds.
    form.setFieldsValue({
      ...mod,
      hook: mod.hook ?? { question: '', answer: '' },
      outcomes: mod.outcomes ?? [],
      reviews: mod.reviews ?? [],
      keyStatements: mod.keyStatements ?? [],
    });
    setModalOpen(true);
  };

  const handleSave = async (values: ModuleFormValues) => {
    setSaving(true);
    try {
      if (editing) {
        await mApi().update({ ...values, id: editing.id, courseId: Number(courseId) });
        message.success('Module updated');
      } else {
        await mApi().create({ ...values, courseId: Number(courseId) });
        message.success('Module created');
      }
      setModalOpen(false);
      fetchData();
    } catch {
      message.error('Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<CourseModule> = [
    { title: '', key: 'sort', width: 40, align: 'center', render: () => <DragHandle /> },
    { title: '#', key: 'idx', width: 60, render: (_: unknown, __: CourseModule, index: number) => index + 1 },
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: 'Title', dataIndex: 'title' },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    { title: 'Price', dataIndex: 'priceRub', key: 'priceRub', width: 100, render: (v: number) => `${v} ₽` },
    { title: 'Disc.', dataIndex: 'discountPercent', key: 'discountPercent', width: 80, render: (v: number | null) => v ? `−${v}%` : '—' },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      filters: STATUS_OPTIONS.map(s => ({ text: s, value: s })),
      onFilter: (value, record) => record.status === value,
      render: (s: Status) => <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
    },
    {
      title: '',
      width: 160,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit module">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => { e.stopPropagation(); openEdit(record); }}
            />
          </Tooltip>
          <Button
            size="small"
            type="link"
            icon={<RightOutlined />}
            onClick={() => navigate(`/courses/${courseId}/modules/${record.id}/lessons`)}
          >
            Lessons
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: <Link to="/courses">Courses</Link> },
            { title: course?.title ?? `Course ${courseId}` },
            { title: 'Modules' },
          ]}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>
            Modules{course ? ` — ${course.title}` : ''}
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New Module
          </Button>
        </div>

        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <SortableContext items={modules.map((m) => String(m.id))} strategy={verticalListSortingStrategy}>
            <Table
              columns={columns}
              dataSource={modules}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 25, showSizeChanger: true }}
              components={{ body: { row: SortableRow } }}
            />
          </SortableContext>
        </DndContext>
      </Space>

      <Modal
        title={editing ? 'Edit Module' : 'New Module'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        destroyOnClose
        okText={editing ? 'Save' : 'Create'}
        width={720}
      >
        <Form form={form} layout="vertical" onFinish={handleSave} style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input placeholder="e.g. Getting Started" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Short description of the module" />
          </Form.Item>
          <Form.Item name="descriptionExtended" label="Extended Description">
            <Input.TextArea rows={5} placeholder="Long-form description of the module" />
          </Form.Item>
          <Form.Item name="imageUrl" label="Image URL">
            <Input placeholder="https://example.com/image.png" />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={STATUS_OPTIONS.map(s => ({
                value: s,
                label: <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
              }))}
            />
          </Form.Item>
          <Form.Item name="group" label="Group" rules={[{ required: true }]}>
            <Select options={GROUP_OPTIONS.map(g => ({ value: g, label: g }))} />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sort Order" rules={[{ required: true, message: 'Sort order is required' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Price (RUB)" name="priceRub" rules={[{ required: true, type: 'number', min: 0 }]}>
            <InputNumber min={0} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="Discount (%)" name="discountPercent" rules={[{ type: 'number', min: 0, max: 99 }]}>
            <InputNumber min={0} max={99} style={{ width: 200 }} />
          </Form.Item>

          <Divider>Hook</Divider>
          <Space style={{ width: '100%' }} styles={{ item: { flex: 1 } }}>
            <Form.Item name={['hook', 'question']} label="Question" style={{ flex: 1 }}>
              <Input placeholder="What will you learn?" />
            </Form.Item>
            <Form.Item name={['hook', 'answer']} label="Answer" style={{ flex: 1 }}>
              <Input placeholder="Swift fundamentals" />
            </Form.Item>
          </Space>

          <Divider>Outcomes</Divider>
          <Form.List name="outcomes">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item name={[name, 'title']} rules={[{ required: true, message: 'Title required' }]}>
                      <Input placeholder="Title" style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item name={[name, 'subtitle']}>
                      <Input placeholder="Subtitle" style={{ width: 220 }} />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} size="small">
                  Add outcome
                </Button>
              </>
            )}
          </Form.List>

          <Divider>Reviews</Divider>
          <Form.List name="reviews">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }} wrap>
                    <Form.Item name={[name, 'name']} rules={[{ required: true, message: 'Name required' }]}>
                      <Input placeholder="Name" style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name={[name, 'meta']}>
                      <Input placeholder="Meta (e.g. 2 weeks ago)" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item name={[name, 'text']}>
                      <Input placeholder="Review text" style={{ width: 180 }} />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} size="small">
                  Add review
                </Button>
              </>
            )}
          </Form.List>

          <Divider>Key Statements</Divider>
          <Form.List name="keyStatements">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item name={name} rules={[{ required: true, message: 'Statement required' }]}>
                      <Input placeholder="Provocative thesis statement" style={{ width: 500 }} />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} size="small">
                  Add statement
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </>
  );
};

export default ModulesPage;
